import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { 
  Beaker, Calculator, Scale, Utensils, ClipboardList, Info, AlertTriangle, 
  CheckCircle, XCircle, ArrowRight, RefreshCcw, 
  Lightbulb, ShieldAlert, Sparkles, ChevronRight,
  ChevronLeft, Timer, Target, Zap, Loader2, Gauge, Wand2, X, Send,
  Cpu, Brain, Microscope, Activity, ShieldCheck, Lock, Droplets, Thermometer,
  TrendingUp, FileText, ClipboardCheck, Dumbbell, Eye, AlertCircle, Volume2
} from 'lucide-react';
import { StorageService } from '../services/storageService';
import { SimulatorScenario, SimulatorCategory } from '../types';
import { GamificationService } from '../services/gamificationService';
import { AdUnit } from '../components/AdUnit';
import { generateAISimulatorScenario } from '../services/geminiService';
import { useAuth } from '../components/AuthBridge';
import { SponsoredService } from '../services/sponsoredService';
import { ModuleHeader } from '../components/ModuleHeader';
import { AdvanceSuggestionPanel } from '../components/AdvanceSuggestionPanel';
import { PageLoader } from '../components/PageLoader';
import { useThemeTokens } from '../hooks/useActiveTheme';

export const SimulatorLab: React.FC = () => {
  const { certId } = useParams<{ certId: string }>();
  const { user } = useAuth();
  const config = SponsoredService.getConfig();
  const tokens = useThemeTokens();
  const [scenarios, setScenarios] = useState<SimulatorScenario[]>([]);
  const [activeScenario, setActiveScenario] = useState<SimulatorScenario | null>(null);
  const [userInput, setUserInput] = useState<string>('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [activeTab, setActiveTab] = useState<SimulatorCategory | 'all'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showHint, setShowHint] = useState(false);
  const [status, setStatus] = useState<{type: 'error' | 'success', text: string} | null>(null);
  
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [aiContext, setAiContext] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Gamification Stats Live Tracking
  const [gamificationStats, setGamificationStats] = useState(() => GamificationService.getStats());

  // Interactive Cheat Sheet Sandbox Tool States
  const [calcUnit, setCalcUnit] = useState<'fluid' | 'weight' | 'temp'>('fluid');
  const [calcVal, setCalcVal] = useState<string>('1');
  const [showReferenceHUD, setShowReferenceHUD] = useState(true);

  // Speech Synthesis active state
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    const handleGamificationUpdate = () => {
      setGamificationStats(GamificationService.getStats());
    };
    window.addEventListener('gamification-updated', handleGamificationUpdate);
    return () => window.removeEventListener('gamification-updated', handleGamificationUpdate);
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await StorageService.getSimulatorScenarios(certId || 'cna');
        setScenarios(data || []);
      } catch (err) {
        console.error("Failed to load scenarios", err);
        setStatus({ type: 'error', text: 'Registry Access Interrupted: Could not load practice problems.' });
      } finally {
        setIsLoading(false);
      }
    };
    load();
    window.addEventListener('simulator-updated', load);
    return () => window.removeEventListener('simulator-updated', load);
  }, [certId]);

  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  }, [activeScenario]);

  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (activeScenario) {
      window.dispatchEvent(
        new CustomEvent('update-breadcrumbs-nesting', {
          detail: {
            currentPageKey: 'simulator',
            level2Label: activeScenario.title,
            onBackAction: 'simulator-back-action',
          },
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('update-breadcrumbs-nesting', {
          detail: null,
        })
      );
    }
  }, [activeScenario]);

  useEffect(() => {
    const handleBack = () => {
      handleReset();
    };
    window.addEventListener('simulator-back-action', handleBack);
    return () => {
      window.removeEventListener('simulator-back-action', handleBack);
    };
  }, [activeScenario]);

  const handleSpeakText = (textToSpeak: string) => {
    if ('speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      setIsSpeaking(true);
      window.speechSynthesis.speak(utterance);
    }
  };

  const filteredScenarios = useMemo(() => {
    if (activeTab === 'all') return scenarios;
    return scenarios.filter(s => s.category === activeTab);
  }, [scenarios, activeTab]);

  const handleStartScenario = (scenario: SimulatorScenario) => {
    setActiveScenario(scenario);
    setUserInput('');
    setIsAnswered(false);
    setIsCorrect(false);
    setShowAiGenerator(false);
    setShowHint(false);
    setStatus(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGenerateAiDrill = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!aiContext.trim() || isGenerating) return;

    setIsGenerating(true);
    setStatus(null);
    try {
      const scenario = await generateAISimulatorScenario(aiContext);
      handleStartScenario(scenario);
      GamificationService.addPoints(10);
    } catch (error) {
      setStatus({ type: 'error', text: 'Logical Engine Overloaded: The AI assistant is busy. Please try again.' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = () => {
    if (!activeScenario || isAnswered) return;
    const value = parseFloat(userInput);
    if (isNaN(value)) {
        setStatus({ type: 'error', text: 'Please enter a valid numeric value.' });
        return;
    }
    const correct = Math.abs(value - activeScenario.correctValue) < 0.01;
    setIsCorrect(correct);
    setIsAnswered(true);
    if (correct) {
      GamificationService.addPoints(25);
    }
  };

  const handleReset = () => {
    setActiveScenario(null);
    setStatus(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryConfig = (category: string) => {
    switch (category) {
      case 'weight': 
        return { 
          icon: Scale, 
          label: 'Weight & Scales', 
          badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-700 dark:bg-indigo-950/20 dark:border-indigo-900/50 dark:text-indigo-400',
          hoverColor: 'hover:border-indigo-300 dark:hover:border-indigo-700',
          textColor: 'text-indigo-600',
          conversionRule: '1 kg = 2.2 lbs'
        };
      case 'nutrition': 
        return { 
          icon: Utensils, 
          label: 'Liquid Intake', 
          badgeColor: 'bg-sky-50 border-sky-100 text-sky-700 dark:bg-sky-950/20 dark:border-sky-900/50 dark:text-sky-450',
          hoverColor: 'hover:border-sky-300 dark:hover:border-sky-700',
          textColor: 'text-sky-650',
          conversionRule: '1 oz = 30 mL'
        };
      case 'output': 
        return { 
          icon: Droplets, 
          label: 'Fluid Output', 
          badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/20 dark:border-rose-900/50 dark:text-rose-400',
          hoverColor: 'hover:border-rose-300 dark:hover:border-rose-700',
          textColor: 'text-rose-600',
          conversionRule: '1 cc = 1 mL'
        };
      case 'vitals': 
        return { 
          icon: Thermometer, 
          label: 'Vital Signs', 
          badgeColor: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-950/20 dark:border-amber-900/50 dark:text-amber-400',
          hoverColor: 'hover:border-amber-300 dark:hover:border-amber-700',
          textColor: 'text-amber-600',
          conversionRule: '°C ⇄ °F, Ranges'
        };
      case 'emergency': 
        return { 
          icon: ShieldAlert, 
          label: 'Emergency & Limits', 
          badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-900/50 dark:text-emerald-400',
          hoverColor: 'hover:border-emerald-300 dark:hover:border-emerald-700',
          textColor: 'text-emerald-650',
          conversionRule: 'Threshold Bounds'
        };
      case 'charting': 
        return { 
          icon: ClipboardCheck, 
          label: 'Clinical Recording', 
          badgeColor: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-950/20 dark:border-violet-900/50 dark:text-violet-400',
          hoverColor: 'hover:border-violet-300 dark:hover:border-violet-700',
          textColor: 'text-violet-600',
          conversionRule: 'Military Time / I&O'
        };
      default: 
        return { 
          icon: Calculator, 
          label: 'Medical Metrics', 
          badgeColor: 'bg-slate-50 border-slate-100 text-slate-700 dark:bg-slate-950/20 dark:border-slate-900/50 dark:text-slate-400',
          hoverColor: 'hover:border-slate-300 dark:hover:border-slate-700',
          textColor: 'text-slate-650',
          conversionRule: 'Conversion calibration'
        };
    }
  };

  const calculatedReferenceOutput = useMemo(() => {
    const val = parseFloat(calcVal);
    if (isNaN(val)) return 'Enter numbers...';
    if (calcUnit === 'fluid') {
      const ml = Math.round(val * 30);
      return `${val} oz = ${ml} mL (or cc)`;
    } else if (calcUnit === 'weight') {
      const kg = (val / 2.2).toFixed(1);
      return `${val} lbs = ${kg} kg`;
    } else if (calcUnit === 'temp') {
      const c = ((val - 32) * 5/9).toFixed(1);
      return `${val} °F = ${c} °C`;
    }
    return '';
  }, [calcUnit, calcVal]);

  if (isLoading) return <PageLoader message="Initializing Mathematical Registry..." />;

  return (
    <div className="relative flex flex-col space-y-4 md:space-y-6 animate-fade-in pb-24 text-slate-950 dark:text-slate-50 max-w-7xl mx-auto">
      <div className="w-full">
        {!activeScenario && <AdUnit placement="math_lab_dashboard" />}
      </div>
      
      {!activeScenario && (
        <div className="w-full">
          <ModuleHeader 
            icon={Beaker}
            title="Math Lab"
            description="Practice nursing math like measuring fluids, liquid ounces, and body weight. These simple practice drills help you get perfectly accurate answers for your test."
            badge="Useful Math"
            color="indigo"
          />
        </div>
      )}

      {status && (
        <div className="w-full">
          <div className={`p-4 rounded-2xl flex items-center gap-3 animate-pop shadow-lg border-2 ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'} sticky top-4 z-[60]`}>
             {status.type === 'error' ? <AlertTriangle size={18} className="shrink-0" /> : <CheckCircle size={18} className="shrink-0" />}
             <p className="text-[10px] font-black tracking-widest flex-1 leading-tight">{status.text}</p>
             <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-lg"><X size={14}/></button>
          </div>
        </div>
      )}

      {/* AI GENERATOR MODAL */}
      {showAiGenerator && config.isMathLabAiEnabled && (
        <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-xl flex items-start justify-center p-4 pt-10 md:pt-16 overflow-y-auto custom-scrollbar">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] md:rounded-[2.5rem] shadow-2xl w-full max-w-xl animate-pop overflow-hidden border border-slate-100 dark:border-slate-800">
            <div className="relative p-5 md:p-6 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900 text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-[80px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="relative z-10 flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-xl shadow-xl ring-1 ring-white/20">
                    <Sparkles className="text-white animate-pulse" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-black tracking-tight mb-0.5">AI Math Calibration</h3>
                    <p className="text-[8px] font-black text-indigo-200 tracking-[0.3em] flex items-center gap-1.5"><Cpu size={10} /> Smart Formulation</p>
                  </div>
                </div>
                <button onClick={() => setShowAiGenerator(false)} className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/60 hover:text-white transition-all active:scale-90"><X size={20} /></button>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-6">
              <div className="space-y-3">
                <label className="text-[9px] font-black text-slate-400 tracking-widest ml-1 flex items-center gap-2"><Microscope size={12} className="text-indigo-500" /> Focus Domain / Instructions</label>
                <textarea 
                    value={aiContext} 
                    onChange={e => setAiContext(e.target.value)} 
                    placeholder="e.g., Intake & Output liquid conversions, measuring urine in CCs, converting wheelchair occupant weight..." 
                    className="w-full p-4 md:p-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl outline-none font-medium text-sm md:text-base text-slate-800 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-700 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all h-32 md:h-40" 
                    autoFocus 
                />
              </div>

              <button onClick={() => handleGenerateAiDrill()} disabled={isGenerating || !aiContext.trim()} className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl md:rounded-2xl font-black text-xs md:text-sm tracking-[0.2em] shadow-2xl transition-all active:scale-95 flex items-center justify-center gap-3 border-b-4 border-indigo-800">
                {isGenerating ? <><Loader2 size={18} className="animate-spin text-white" /> Synthesizing parameters...</> : <><Zap size={18} className="text-amber-350" /> Generate Dynamic Drill</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {!activeScenario ? (
        <div className="space-y-10">
          {/* UPDATED HERO BAR & GAMIFICATION PANEL */}
          <section className={`${tokens.heroBannerBg} rounded-[2rem] md:rounded-[2.5rem] p-6 md:p-8 text-white shadow-2xl relative overflow-hidden group border border-slate-800 shrink-0`}>
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 blur-[100px] pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/5 blur-[80px] pointer-events-none translate-y-1/2 -translate-x-1/3"></div>
            
            <div className="relative z-10 flex flex-col lg:flex-row justify-between items-stretch gap-6 md:gap-8">
                <div className="flex-1 flex flex-col justify-center min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 text-[8px] font-black rounded-lg border border-indigo-500/30 flex items-center gap-1.5 uppercase tracking-wider">
                            <Activity size={10} className="animate-pulse" /> Precision Training
                        </span>
                        <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 text-[8px] font-black rounded-lg border border-cyan-500/30 flex items-center gap-1.5 uppercase tracking-wider">
                            <Gauge size={10} /> Live Calibration
                        </span>
                    </div>
                    <h1 className="text-3xl md:text-5xl font-black mb-2 tracking-tight leading-none text-white">Math & Metrics Lab</h1>
                    <p className="text-slate-350 text-xs md:text-base max-w-2xl leading-relaxed opacity-90">
                      Standardize and calibrate your nurse medication math, I&O liquid totals, conversion formulas, and clinical measurements before board day.
                    </p>
                </div>

                {/* Gamification Live Dashboard Widget */}
                <div className="shrink-0 flex flex-col md:flex-row items-center gap-4 bg-slate-850/80 p-4 md:p-6 rounded-[1.5rem] border border-slate-800 backdrop-blur-md">
                    <div className="flex flex-row md:flex-col items-center md:items-start gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-650 flex items-center justify-center text-indigo-200">
                        <TrendingUp size={24} />
                      </div>
                      <div>
                        <span className="text-[8px] font-black tracking-widest uppercase text-slate-400">CALIBRATION LEVEL</span>
                        <p className="text-sm font-black text-indigo-300">Level {gamificationStats.level}</p>
                      </div>
                    </div>
                    
                    <div className="h-px md:h-12 w-full md:w-px bg-slate-800"></div>

                    <div>
                      <span className="text-[8px] font-black tracking-widest uppercase text-slate-400">MATH XP POINTS</span>
                      <p className="text-lg font-black text-white">{gamificationStats.points} pts</p>
                    </div>

                    <div className="h-px md:h-12 w-full md:w-px bg-slate-800"></div>

                    <div>
                      <span className="text-[8px] font-black tracking-widest uppercase text-slate-400">DRILLS AVAILABLE</span>
                      <p className="text-lg font-black text-cyan-300">{scenarios.length} protocols</p>
                    </div>
                </div>
            </div>
          </section>

          {/* DUAL PANE LAYOUT: Main categories & scenarios grid on left, Conversion widget on right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* LEFT 8 COLUMNS: CATEGORY & DRILL GRID */}
            <div className="lg:col-span-8 space-y-8">
              
              {/* STICKY CHIP CATEGORY NAVIGATION */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xs font-black tracking-widest uppercase text-slate-450 dark:text-slate-400 flex items-center gap-2">
                    <Calculator size={14} className="text-indigo-500" /> Category Navigation
                  </h2>
                  <span className="text-[10px] font-bold text-slate-400">Filter scenario challenges</span>
                </div>
                
                <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto no-scrollbar gap-1 w-full scroll-smooth">
                    <button 
                      onClick={() => setActiveTab('all')} 
                      className={`px-5 py-3 rounded-xl text-[9px] font-black tracking-widest transition-all whitespace-nowrap ${activeTab === 'all' ? tokens.activeTab : tokens.inactiveTab}`}
                    >
                      ALL DRILLS ({scenarios.length})
                    </button>
                    {(['weight', 'nutrition', 'output', 'vitals', 'emergency', 'charting'] as const).map(tab => {
                        const count = scenarios.filter(s => s.category === tab).length;
                        const cfg = getCategoryConfig(tab);
                        return (
                          <button 
                            key={tab} 
                            onClick={() => setActiveTab(tab)} 
                            className={`px-5 py-3 rounded-xl text-[9px] font-black tracking-widest transition-all whitespace-nowrap flex items-center gap-1.5 ${activeTab === tab ? tokens.activeTab : tokens.inactiveTab}`}
                          >
                            <cfg.icon size={11} />
                            {cfg.label.toUpperCase()} ({count})
                          </button>
                        );
                    })}
                </div>
              </div>

              {/* BENTO-STYLE FILTER INFRASTRUCTURE */}
              {activeTab === 'all' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(['weight', 'nutrition', 'output', 'vitals', 'emergency', 'charting'] as const).map(tab => {
                    const count = scenarios.filter(s => s.category === tab).length;
                    const cfg = getCategoryConfig(tab);
                    return (
                      <button 
                        key={tab}
                        onClick={() => {
                          setActiveTab(tab);
                          window.scrollTo({ top: 380, behavior: 'smooth' });
                        }}
                        className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-400 dark:hover:border-indigo-850 hover:shadow-md text-left flex flex-col justify-between h-24 transition-all hover:-translate-y-0.5 group"
                      >
                        <div className="flex justify-between items-start w-full">
                          <div className={`p-2 rounded-xl ${cfg.badgeColor} group-hover:scale-105 transition-transform`}>
                            <cfg.icon size={16} />
                          </div>
                          <span className="text-[10px] text-slate-405 dark:text-slate-500 font-black">{count} drills</span>
                        </div>
                        <div>
                          <h4 className="text-[11px] font-black text-slate-900 dark:text-slate-100 leading-tight truncate">{cfg.label}</h4>
                          <p className="text-[8px] font-bold text-slate-400 tracking-wider truncate">{cfg.conversionRule}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* LIST OF SCENARIOS */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-xs font-black tracking-widest uppercase text-slate-455 dark:text-slate-400 flex items-center gap-2">
                    <ClipboardList size={14} className="text-indigo-500" />
                    Available Lab Protocols {activeTab !== 'all' && `(${activeTab.toUpperCase()})`}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold">Tap a card to initiate simulation</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config.isMathLabAiEnabled && (
                      <button 
                        onClick={() => setShowAiGenerator(true)} 
                        className="bg-gradient-to-br from-indigo-50/50 to-white dark:from-indigo-950/20 dark:to-slate-900 p-6 rounded-3xl border-2 border-dashed border-indigo-200 dark:border-indigo-900/50 hover:border-indigo-400 transition-all text-left flex flex-col justify-between group h-44 cursor-pointer"
                      >
                        <div className="flex justify-between items-start w-full">
                          <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-950 text-indigo-650 dark:text-indigo-450 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform"><Wand2 size={20} /></div>
                          <span className="px-2.5 py-0.5 bg-gradient-to-r from-amber-500 to-indigo-600 text-white text-[7px] font-black rounded-full uppercase tracking-widest shadow-sm">AI Engine</span>
                        </div>
                        <div>
                          <h3 className="text-xs font-black text-slate-900 dark:text-slate-100 mb-1">Synthesize Custom Scenario</h3>
                          <p className="text-[9px] text-slate-500 dark:text-slate-450 font-medium">Generate arbitrary mathematical clinical case values instantly with AI calibration.</p>
                        </div>
                      </button>
                  )}

                  {filteredScenarios.map(s => {
                    const cfg = getCategoryConfig(s.category);
                    const CategoryIcon = cfg.icon;
                    return (
                      <button 
                        key={s.id} 
                        onClick={() => handleStartScenario(s)} 
                        className={`bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 ${tokens.accentBorder} hover:shadow-xl transition-all text-left flex flex-col justify-between group active:scale-98 h-44 relative overflow-hidden`}
                      >
                        <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                           <div className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-[6px] font-black rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">Protocol Loaded</div>
                        </div>
                        <div>
                          <div className="mb-3">
                            <div className={`w-9 h-9 ${cfg.badgeColor} rounded-lg shadow-sm flex items-center justify-center`}>
                                <CategoryIcon size={16} />
                            </div>
                          </div>
                          <h3 className={`text-xs font-black text-slate-900 dark:text-slate-100 leading-snug mb-1 group-hover:${tokens.accentText} transition-colors tracking-tight line-clamp-2`}>{s.title}</h3>
                          <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1">
                            <span>{s.category}</span>
                            <span className="opacity-30">•</span>
                            <span>Target: {s.targetUnit}</span>
                          </p>
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-slate-100 dark:border-slate-800 w-full">
                            <div className="flex gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 group-hover:animate-ping"></div>
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                            </div>
                            <div className={`flex items-center gap-1.5 text-slate-400 group-hover:${tokens.accentText} transition-all text-xs`}>
                              <span className="text-[7.5px] font-black tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity">Start Drill</span>
                              <ArrowRight size={14} />
                            </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* RIGHT 4 COLUMNS: INTERACTIVE reference widget & cheat sheet */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* REFERENCE CALC DRAWER / INTERACTIVE SANDBOX WIDGET */}
              <div className={`${tokens.heroBannerBg} text-white rounded-3xl p-6 shadow-xl border border-white/10 space-y-6`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                     <Calculator size={18} className={`${tokens.accentText} animate-pulse`} />
                     <div>
                       <h3 className="text-[11px] font-black uppercase tracking-widest text-[#a5b4fc]">Conversion Sandbox</h3>
                       <p className="text-[8px] text-[#818cf8] font-bold">CALIBRATE YOUR VALUES</p>
                     </div>
                  </div>
                  <span className="px-2 py-0.5 bg-indigo-950 text-indigo-400 text-[6px] font-black rounded border border-indigo-800">CNA LAB</span>
                </div>

                {/* Conversion tool tab selectors */}
                <div className="grid grid-cols-3 bg-indigo-950 p-1 rounded-xl border border-indigo-900">
                  <button 
                    onClick={() => { setCalcUnit('fluid'); setCalcVal('1'); }} 
                    className={`py-1.5 text-[8.5px] font-black rounded-lg transition-all ${calcUnit === 'fluid' ? 'bg-indigo-650 text-white' : 'text-indigo-300 hover:text-white'}`}
                  >
                    Fluids (oz)
                  </button>
                  <button 
                    onClick={() => { setCalcUnit('weight'); setCalcVal('150'); }} 
                    className={`py-1.5 text-[8.5px] font-black rounded-lg transition-all ${calcUnit === 'weight' ? 'bg-indigo-650 text-white' : 'text-indigo-300 hover:text-white'}`}
                  >
                    Weights (lbs)
                  </button>
                  <button 
                    onClick={() => { setCalcUnit('temp'); setCalcVal('98.6'); }} 
                    className={`py-1.5 text-[8.5px] font-black rounded-lg transition-all ${calcUnit === 'temp' ? 'bg-indigo-650 text-white' : 'text-indigo-300 hover:text-white'}`}
                  >
                    Temp (°F)
                  </button>
                </div>

                {/* Interactive calculator inputs */}
                <div className="space-y-3">
                  <span className="text-[8px] text-[#ccccff] font-bold tracking-wider">ENTER VALUE TO CONVERT:</span>
                  <div className="flex gap-2">
                    <input 
                      type="number"
                      value={calcVal}
                      onChange={e => setCalcVal(e.target.value)}
                      placeholder="0.0"
                      className="w-full bg-indigo-950 border border-indigo-800 rounded-xl p-3 text-white font-black text-xl tabular-nums focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all text-center"
                    />
                    <div className="px-4 py-3 bg-indigo-800 rounded-xl flex items-center justify-center font-black text-xs min-w-[70px]">
                      {calcUnit === 'fluid' ? 'oz' : calcUnit === 'weight' ? 'lbs' : '°F'}
                    </div>
                  </div>
                </div>

                {/* Calculated Result display */}
                <div className="bg-indigo-950 p-4 rounded-2xl border border-indigo-800 text-center space-y-1 block shadow-inner">
                  <p className="text-[7.5px] text-cyan-400 font-extrabold tracking-[0.2em] uppercase">CONVERTED MEASUREMENT</p>
                  <p className="text-base font-black text-white leading-tight tracking-tight">{calculatedReferenceOutput}</p>
                </div>
              </div>

              {/* STATS REFERENCE BENCHMARKS */}
              <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 space-y-6">
                <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-3">
                   <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-455 dark:text-slate-400 flex items-center gap-2">
                     <Brain size={14} className="text-indigo-505" />
                     Clinical Cheat Benchmarks
                   </h3>
                   <span className="text-[8px] bg-slate-100 dark:bg-slate-950 text-slate-500 px-1.5 py-0.5 rounded font-black border border-slate-100 dark:border-slate-800">GUIDE</span>
                </div>

                {/* Benchmarks definitions */}
                <div className="space-y-4">
                  <div className="p-3.5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-850 space-y-2">
                    <p className="text-[9px] font-black text-indigo-650 dark:text-indigo-400 tracking-wider">FLUID CONVERSIONS</p>
                    <ul className="text-[10px] font-bold text-slate-600 dark:text-slate-350 space-y-1.5 list-disc pl-3">
                      <li>1 fluid ounce (oz) = 30 mL (or cc)</li>
                      <li>1 measuring cup = 8 oz = 240 mL</li>
                      <li>1 ordinary glass = 8 oz = 240 mL</li>
                      <li>1 individual soup bowl = 6 oz = 180 mL</li>
                      <li>1 measuring gelatin cup = 4 oz = 120 mL</li>
                    </ul>
                  </div>

                  <div className="p-3.5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-850 space-y-2">
                    <p className="text-[9px] font-black text-indigo-650 dark:text-indigo-400 tracking-wider">WEIGHT CALIBRATION</p>
                    <ul className="text-[10px] font-bold text-slate-600 dark:text-slate-350 space-y-1.5 list-disc pl-3">
                      <li>1 kilogram (kg) = 2.2 pounds (lbs)</li>
                      <li>lbs to kg: Divide by 2.2</li>
                      <li>kg to lbs: Multiply by 2.2</li>
                      <li>1 pound (lb) = 16 ounces (oz)</li>
                      <li>Check scales always at 0 before weighing</li>
                    </ul>
                  </div>

                  <div className="p-3.5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-850 space-y-2">
                    <p className="text-[9px] font-black text-indigo-650 dark:text-indigo-400 tracking-wider">MILITARY TIME & VITALS</p>
                    <div className="text-[10px] font-bold text-slate-600 dark:text-slate-350 space-y-1">
                      <p>• PM hours: Add 12 to standard time (e.g. 1 PM = 1300, 10 PM = 2200)</p>
                      <p className="mt-1">• Temp normal range: 97.6°F to 99.6°F</p>
                      <p>• Pulse normal range: 60 to 100 bpm</p>
                      <p>• Respiration: 12 to 20 breaths/min</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      ) : (
        /* ACTIVE SCENARIO DRILL MODE - COMPLETELY POLISHED & MODERNIZED */
        <div className="w-full">
          <article className="flex-1 flex flex-col animate-pop gap-6">
            
            {/* STICKY ACTIVE BAR */}
            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl flex justify-between items-center z-50 shrink-0 sticky top-0 md:relative md:top-auto">
              <div className="flex items-center gap-3 overflow-hidden">
                <button 
                  onClick={handleReset} 
                  className="flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-extrabold text-[10px] tracking-widest uppercase rounded-xl transition-all shadow-[0_4px_12px_rgba(244,63,94,0.3)] active:scale-95 group shrink-0 border border-rose-400/20"
                  aria-label="Exit current math scenario and return to scenario list"
                >
                  <ChevronLeft size={16} strokeWidth={3} className="group-hover:-translate-x-0.5 transition-transform" />
                  <span className="hidden sm:inline">Close Drill</span>
                </button>
                <div className="min-w-0">
                   <h3 className="text-[9.5px] md:text-xs font-black text-slate-900 dark:text-white tracking-widest truncate max-w-[150px] md:max-w-md mb-0.5">{activeScenario.title.toUpperCase()}</h3>
                   <div className="flex items-center gap-1.5">
                     <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></div>
                     <span className="text-[7.5px] font-black text-indigo-650 dark:text-indigo-405 tracking-[0.2em] uppercase">Interactive Calibrator Live</span>
                   </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowReferenceHUD(!showReferenceHUD)} 
                  className={`px-3.5 py-1.5 rounded-xl text-[8px] md:text-[9.5px] font-black tracking-widest transition-all ${showReferenceHUD ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 border border-indigo-200' : 'bg-slate-50 dark:bg-slate-950 text-slate-500 hover:bg-slate-100'}`}
                >
                  {showReferenceHUD ? 'Hide Sandbox' : 'Sandbox Tool'}
                </button>
                <button 
                  onClick={() => setShowHint(!showHint)} 
                  className={`px-3.5 py-1.5 rounded-xl text-[8px] md:text-[9.5px] font-black tracking-widest transition-all shadow-sm ${showHint ? 'bg-amber-500 text-white font-extrabold' : 'bg-slate-100 dark:bg-slate-955 text-slate-550 dark:text-slate-400 hover:bg-slate-200'}`}
                >
                  <Lightbulb size={12} className="inline mr-1.5"/> {showHint ? 'Hide Logic' : 'Tip Guide'}
                </button>
              </div>
            </div>

            {/* STAGE MAIN BODY: Dual Panel (Main Drill on Left, Sandbox HUD & Safety elements on right) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-12 items-start">
               
               {/* MAIN DRILL WORKSPACE CARD (7 Columns) */}
               <div className="lg:col-span-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 md:p-10 shadow-sm space-y-8">
                  
                  {/* Case text with text to speech */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className={`px-3 py-1 ${tokens.badgeBg} ${tokens.accentText} text-[8px] font-black rounded-lg border border-slate-200 dark:border-slate-800 tracking-[0.2em] uppercase`}>
                        Case Scenario Protocol
                      </span>
                      <button 
                        onClick={() => handleSpeakText(activeScenario.scenarioText)}
                        className={`p-2 rounded-xl transition-all border ${isSpeaking ? 'bg-rose-50 text-rose-500 animate-pulse border-rose-100' : 'bg-slate-100 dark:bg-slate-950 text-slate-500 dark:text-slate-400 hover:text-indigo-650'}`}
                        title={isSpeaking ? "Stop speech" : "Read scenario text aloud"}
                      >
                        <Volume2 size={16} />
                      </button>
                    </div>

                    <p className={`text-base md:text-xl font-bold text-slate-900 dark:text-slate-100 leading-relaxed tracking-tight border-l-4 ${tokens.accentBorder} pl-6 py-2`}>
                        "{activeScenario.scenarioText}"
                    </p>
                  </div>

                  {/* Input form */}
                  <div className="space-y-4 bg-slate-50 dark:bg-slate-950/60 p-6 md:p-8 rounded-[2rem] border border-slate-150 dark:border-slate-850 shadow-inner">
                    <label className="text-[9px] md:text-[10px] font-black text-slate-400 dark:text-slate-450 tracking-widest ml-1 flex items-center gap-2">
                      <Target size={14} className={tokens.accentText} /> 
                      Target Measurement Unit: <span className="text-slate-900 dark:text-slate-100 font-extrabold">{activeScenario.targetUnit}</span>
                    </label>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="relative flex-1">
                        <input 
                          type="number" 
                          inputMode="decimal"
                          value={userInput} 
                          onChange={(e) => setUserInput(e.target.value)} 
                          disabled={isAnswered} 
                          placeholder="0.00" 
                          className={`w-full p-4 md:p-6 bg-white dark:bg-slate-900 border-2 rounded-2xl font-black text-3xl md:text-4xl tabular-nums transition-all outline-none text-center sm:text-left ${isAnswered ? (isCorrect ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400' : 'border-rose-500 text-rose-600 dark:text-rose-400') : 'border-slate-150 dark:border-slate-800 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-950/40 shadow-sm focus:shadow-md'}`}
                          autoFocus
                        />
                      </div>
                      
                      <div className="px-6 py-4 sm:py-0 bg-slate-900 dark:bg-slate-950 text-white rounded-2xl flex flex-col items-center justify-center min-w-[120px] border-b-4 border-slate-950 shadow-md">
                        <span className="text-[7.5px] font-black text-indigo-305 tracking-[0.2em] mb-0.5">METRIC LABEL</span>
                        <span className="font-black text-base text-cyan-300 tracking-tight">{activeScenario.unitLabel}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action or verified visual cards */}
                  <div className="pt-4">
                    {!isAnswered ? (
                      <button 
                          onClick={handleSubmit} 
                          disabled={!userInput} 
                          className={`w-full py-5 md:py-6 ${tokens.primaryBtn} text-white rounded-2xl font-black text-sm md:text-base tracking-[0.25em] shadow-lg hover:shadow-xl transition-all border-b-4 active:scale-98 disabled:opacity-40 disabled:shadow-none`}
                      >
                          SUBMIT VALUE FOR CLINICAL VERIFICATION <ArrowRight size={18} className="inline ml-1" />
                      </button>
                    ) : (
                      <div className="space-y-4 animate-slide-up">
                        <div className={`p-6 rounded-[2rem] border-2 flex items-center gap-5 shadow-sm ${isCorrect ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-250' : 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-250'}`}>
                           <div className={`w-12 h-12 rounded-xl shadow-md flex items-center justify-center shrink-0 ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                              {isCorrect ? <CheckCircle size={24}/> : <XCircle size={24}/>}
                           </div>
                           <div className="flex-1 min-w-0">
                                <h4 className={`text-base font-black tracking-tight ${isCorrect ? 'text-emerald-900 dark:text-emerald-300' : 'text-rose-900 dark:text-rose-300'}`}>
                                  {isCorrect ? 'Measurement Core Checked & Correct' : 'Unit Mismatch Detected'}
                                </h4>
                                <p className="text-[10px] md:text-xs font-bold text-slate-505 dark:text-slate-400 mt-0.5">
                                  {isCorrect ? `Perfect. The correct answer was exactly ${activeScenario.correctValue} ${activeScenario.unitLabel}.` : `The clinical calculation requires ${activeScenario.correctValue} ${activeScenario.unitLabel}. Review the rationale.`}
                                </p>
                           </div>
                        </div>
                        <button 
                          onClick={handleReset} 
                          className="w-full py-4 bg-slate-900 hover:bg-slate-800 text-white border-b-4 border-black dark:border-slate-950 rounded-xl font-black text-xs md:text-sm tracking-[0.20em] transition-all active:scale-95 shadow-md flex items-center justify-center gap-2"
                        >
                          PROCEED TO NEXT DRILL <ChevronRight size={16}/>
                        </button>

                        <div className="mt-6 pt-6 border-t border-slate-150 dark:border-slate-850 text-left">
                           <AdvanceSuggestionPanel 
                             source="simulator" 
                             score={isCorrect ? 100 : 0} 
                             moduleId={activeScenario.id}
                             moduleTitle={activeScenario.title}
                           />
                        </div>
                      </div>
                    )}
                  </div>

               </div>

               {/* SIDE PANELS FOR INTEGRATED WORKSPACE (5 Columns) */}
               <div className="lg:col-span-5 space-y-6">

                  <AdUnit placement="math_lab_sidebar" />

                  {/* MINI REFERENCE HUD CONDITIONAL DRAWER */}
                  {showReferenceHUD && (
                    <div className="bg-slate-900 text-white rounded-3xl p-5 shadow-lg border border-slate-800 space-y-4 animate-slide-down">
                      <div className="flex justify-between items-center border-b border-slate-800 pb-2.5">
                        <div className="flex items-center gap-2">
                          <Calculator size={16} className="text-indigo-400" />
                          <h4 className="text-[10px] font-black tracking-wider text-slate-300">SANDBOX REFERENCE UNIT</h4>
                        </div>
                        <button onClick={() => setShowReferenceHUD(false)} className="p-1 text-slate-500 hover:text-white" title="Hide Reference tool">
                          <X size={12} />
                        </button>
                      </div>

                      <div className="grid grid-cols-3 bg-slate-955 p-0.5 rounded-lg text-[8px] font-black">
                        <button onClick={() => setCalcUnit('fluid')} className={`py-1 rounded ${calcUnit === 'fluid' ? 'bg-indigo-650' : 'text-slate-400'}`}>FLUID</button>
                        <button onClick={() => setCalcUnit('weight')} className={`py-1 rounded ${calcUnit === 'weight' ? 'bg-indigo-650' : 'text-slate-400'}`}>WEIGHT</button>
                        <button onClick={() => setCalcUnit('temp')} className={`py-1 rounded ${calcUnit === 'temp' ? 'bg-indigo-650' : 'text-slate-400'}`}>TEMP</button>
                      </div>

                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input 
                            type="number" 
                            className="bg-slate-950 text-white font-extrabold text-sm p-2 rounded-lg flex-1 text-center" 
                            value={calcVal} 
                            onChange={e => setCalcVal(e.target.value)} 
                          />
                          <span className="px-3 bg-slate-800 text-[10px] font-black rounded-lg flex items-center">{calcUnit === 'fluid' ? 'oz' : calcUnit === 'weight' ? 'lbs' : '°F'}</span>
                        </div>
                        <div className="bg-slate-950 p-3 rounded-xl text-center">
                          <span className="text-[8px] text-indigo-400 font-extrabold block">STANDARDIZED INTUITION</span>
                          <span className="text-[11px] font-extrabold text-white">{calculatedReferenceOutput}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SAFETY INTEGRITY HIGHLIGHTER */}
                  <div className="p-6 bg-red-50 dark:bg-red-950/20 border border-red-150 dark:border-red-900 rounded-3xl shadow-sm relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-3 bg-red-100 dark:bg-red-900/45 rounded-bl-3xl"><AlertTriangle size={18} className="text-red-500 animate-pulse" /></div>
                      <h4 className="text-[9.5px] font-black text-red-700 dark:text-red-400 tracking-widest mb-2 uppercase flex items-center gap-1.5">
                        Clinical Safety Warning
                      </h4>
                      <p className="text-xs font-bold text-red-900 dark:text-red-200 leading-relaxed max-w-[85%]">
                        "{activeScenario.safetyImpact}"
                      </p>
                  </div>

                  {/* RATIONALE EXPLAINER CHIP */}
                  <div className="p-6 md:p-8 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                     <div className="flex items-center justify-between mb-5">
                        <h4 className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 tracking-[0.2em] uppercase flex items-center gap-2">
                          <Brain size={16}/> Clinical Solution Rationale
                        </h4>
                     </div>
                     
                     {(showHint || isAnswered) ? (
                       <div className="space-y-5 animate-slide-up">
                          <p className="text-xs md:text-sm font-bold text-slate-650 dark:text-slate-300 leading-relaxed border-l-4 border-indigo-500 pl-4 py-1">
                            "{activeScenario.explanation}"
                          </p>
                          <div className="space-y-2">
                             <p className="text-[8.5px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase">Clinical Formula Matrix:</p>
                             {activeScenario.stepByStep.map((step, idx) => (
                               <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-slate-800 flex items-center gap-3">
                                  <div className="w-5 h-5 rounded-lg bg-indigo-100 dark:bg-indigo-950 text-indigo-650 dark:text-indigo-400 flex items-center justify-center font-black text-[9px] shrink-0">{idx + 1}</div>
                                  <span className="text-[10px] md:text-[11.5px] font-bold text-slate-800 dark:text-slate-200 leading-tight">{step}</span>
                               </div>
                             ))}
                          </div>
                       </div>
                     ) : (
                       <div className="py-8 flex flex-col items-center justify-center opacity-30 grayscale gap-3 text-center">
                          <Lock size={32} className="text-slate-400" />
                          <p className="text-[9px] font-black tracking-[0.25em] uppercase text-slate-405 leading-relaxed">
                            Logic Matrix Encrypted<br/>
                            <span className="text-[7.5px] font-bold opacity-60">Answer correctly or tap "Tip Guide" to decrypt</span>
                          </p>
                       </div>
                     )}
                  </div>

               </div>
            </div>

          </article>
        </div>
      )}
    </div>
  );
};
